import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import {
  Loader2Icon,
  ArrowLeftIcon,
  CheckIcon,
  RefreshCwIcon,
} from "lucide-react";

interface CodeStepProps {
  email: string;
  onVerify: (code: string) => Promise<void>;
  onBack: () => void;
  onResend: () => Promise<void>;
  isVerifying: boolean;
  isResending: boolean;
  error: string | null;
}

export function CodeStep({
  email,
  onVerify,
  onBack,
  onResend,
  isVerifying,
  isResending,
  error,
}: CodeStepProps) {
  const [digits, setDigits] = useState<string[]>(["", "", "", "", "", ""]);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleDigitChange = useCallback(
    (index: number, value: string) => {
      // Only allow numbers
      const digit = value.replace(/\D/g, "").slice(-1);
      const newDigits = [...digits];
      newDigits[index] = digit;
      setDigits(newDigits);

      // Auto-advance to next input
      if (digit && index < 5) {
        inputRefs.current[index + 1]?.focus();
        setFocusedIndex(index + 1);
      }

      // Auto-submit when all digits entered
      if (digit && index === 5) {
        const code = newDigits.join("");
        if (code.length === 6) {
          onVerify(code);
        }
      }
    },
    [digits, onVerify]
  );

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Backspace" && !digits[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
        setFocusedIndex(index - 1);
      } else if (e.key === "ArrowLeft" && index > 0) {
        inputRefs.current[index - 1]?.focus();
        setFocusedIndex(index - 1);
      } else if (e.key === "ArrowRight" && index < 5) {
        inputRefs.current[index + 1]?.focus();
        setFocusedIndex(index + 1);
      }
    },
    [digits]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      e.preventDefault();
      const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
      if (pasted) {
        const newDigits = pasted.split("").concat(Array(6).fill("")).slice(0, 6);
        setDigits(newDigits);
        const lastFilledIndex = Math.min(pasted.length - 1, 5);
        inputRefs.current[lastFilledIndex]?.focus();
        setFocusedIndex(lastFilledIndex);

        // Auto-submit if 6 digits pasted
        if (pasted.length === 6) {
          onVerify(pasted);
        }
      }
    },
    [onVerify]
  );

  const handleResend = async () => {
    await onResend();
    setResendCooldown(60);
    setDigits(["", "", "", "", "", ""]);
    inputRefs.current[0]?.focus();
    setFocusedIndex(0);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const code = digits.join("");
    if (code.length === 6) {
      onVerify(code);
    }
  };

  const code = digits.join("");
  const isComplete = code.length === 6;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Email confirmation */}
      <div className="text-center space-y-1">
        <p className="text-sm text-muted-foreground">
          We sent a verification code to
        </p>
        <p className="font-medium text-foreground tracking-wide">{email}</p>
      </div>

      {/* 6-digit code input boxes */}
      <div className="space-y-3">
        <div className="flex justify-center gap-2 sm:gap-3" onPaste={handlePaste}>
          {digits.map((digit, index) => (
            <div key={index} className="relative">
              {/* Animated focus ring */}
              <div
                className={`
                  absolute -inset-px rounded-lg transition-all duration-200
                  ${
                    focusedIndex === index
                      ? "bg-gradient-to-br from-primary/30 to-primary/10"
                      : ""
                  }
                `}
              />
              <input
                ref={(el) => {
                  inputRefs.current[index] = el;
                }}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                value={digit}
                onChange={(e) => handleDigitChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onFocus={() => setFocusedIndex(index)}
                disabled={isVerifying}
                className={`
                  relative w-11 h-14 sm:w-12 sm:h-16
                  text-center text-2xl font-mono font-semibold
                  bg-card border border-border rounded-lg
                  text-foreground
                  outline-none transition-all duration-200
                  focus:border-primary/50 focus:ring-0
                  disabled:opacity-50 disabled:cursor-not-allowed
                  ${digit ? "border-primary/30" : ""}
                `}
                aria-label={`Digit ${index + 1}`}
              />
              {/* Subtle dot indicator when filled */}
              {digit && (
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 size-1 rounded-full bg-primary/60" />
              )}
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground text-center">
          Enter the 6-digit code from your email
        </p>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
          <div className="size-1.5 rounded-full bg-destructive mt-1.5 shrink-0" />
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      <Button
        type="submit"
        className="w-full h-12 text-base font-medium"
        disabled={!isComplete || isVerifying}
      >
        {isVerifying ? (
          <>
            <Loader2Icon className="size-4 animate-spin" />
            <span>Verifying...</span>
          </>
        ) : (
          <>
            <CheckIcon className="size-4" />
            <span>Verify & Continue</span>
          </>
        )}
      </Button>

      {/* Back and Resend actions */}
      <div className="flex items-center justify-between pt-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onBack}
          disabled={isVerifying}
          className="text-muted-foreground hover:text-foreground"
        >
          <ArrowLeftIcon className="size-4" />
          <span>Back</span>
        </Button>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleResend}
          disabled={isResending || resendCooldown > 0 || isVerifying}
          className="text-muted-foreground hover:text-foreground"
        >
          {isResending ? (
            <Loader2Icon className="size-4 animate-spin" />
          ) : (
            <RefreshCwIcon className="size-3.5" />
          )}
          <span>
            {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
          </span>
        </Button>
      </div>
    </form>
  );
}
